/**
 * PROMPT 5: Workspace Home Page with Modern Shell
 *
 * Layout from top to bottom:
 * 1. Header block - workspace name, owner name, action buttons (Owner only)
 * 2. Start work section - CTA for owner/member, read-only message for guest
 * 3. Workspace notes section - editable by owner, read-only for others
 * 4. Projects snapshot - up to 5 projects (name, status, health label)
 * 5. Members snapshot - up to 5 members with total count
 */
/**
 * PHASE 6 MODULE 2: Workspace Home with Dashboard Widgets
 *
 * Enhanced with:
 * - Widget cards for metrics (active projects, standalone vs linked, work items, risks, conflicts)
 * - Uses workspace home endpoint data
 * - Computes metrics from projects array
 * - Clickable numbers filter projects list
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MoreHorizontal, Copy } from 'lucide-react';
import { taskActivityPhrase } from '@/features/work-management/taskActivityFormat';
import { formatCalendarDate } from '@/utils/calendarDate';
import { useAuth } from '@/state/AuthContext';
import { useWorkspaceRole } from '@/hooks/useWorkspaceRole';
import { useWorkspaceStore } from '@/state/workspace.store';
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions';
import { getWorkspace, updateWorkspace, listProjects, listWorkspaceMembers, WorkspaceApiData, WorkspaceMember } from '@/features/workspaces/workspace.api';
import { TemplateCenterModal } from '@/features/templates/components/TemplateCenterModal';
import { DuplicateProjectModal } from '@/features/projects/components/DuplicateProjectModal';
import { WorkspaceMemberInviteModal } from '@/features/workspaces/components/WorkspaceMemberInviteModal';
import { Button } from '@/components/ui/Button';
import { Link } from 'react-router-dom';
import { addWorkspaceMember } from '@/features/workspaces/workspace.api';
import { toast } from 'sonner';
import { mapAccessLevelToRole } from '@/utils/workspace-access-levels';
import { request } from '@/lib/api';
import { UnavailablePanel, UnavailableValue } from '@/components/ui/feedback/Unavailable';
import { useWorkspaceVisitTracker } from '@/hooks/useWorkspaceVisitTracker';
import { NEW_PROJECT_ACTION_LABEL } from '@/features/templates/labels';
import {
  activeProjectMetric,
  lifecycleHealthLabel,
  projectStateLabel,
} from '@/features/projects/projectStateDisplay';

// Use the Workspace type from workspace.api.ts
type Workspace = WorkspaceApiData;

type Project = {
  id: string;
  name: string;
  /** Legacy. Do not count or label from this. */
  status?: string;
  /** DRAFT | ACTIVE | CLOSING | CLOSED. Absent means not enough data. */
  state?: string | null;
  health?: string;
  programId?: string | null;
  portfolioId?: string | null;
  program?: { id: string; name: string } | null;
  portfolio?: { id: string; name: string } | null;
  deliveryOwner?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    name?: string;
  };
  deliveryOwnerUserId?: string;
};

type ExecutionSummary = {
  version: number;
  /** A count the server did not send is null (Unavailable), never 0. */
  counts: {
    activeProjects: number | null;
    totalWorkItems: number | null;
    overdueWorkItems: number | null;
    dueSoon7Days: number | null;
    inProgress: number | null;
    doneLast7Days: number | null;
  };
  /** null = the list was not sent; [] = a real empty list. */
  topOverdue: Array<{
    id: string;
    title: string;
    dueDate: string | null;
    projectId: string;
    projectName: string;
    assigneeId: string | null;
    assigneeName: string | null;
  }> | null;
  recentActivity: Array<{
    type: string;
    createdAt: string;
    actorUserId: string;
    actorName: string;
    workItemId: string;
    workItemTitle: string;
    projectId: string;
    projectName: string;
  }> | null;
};

type WorkspaceHomeData = {
  workspace: {
    id: string;
    name: string;
    slug: string;
    description: string | null;
    owner: {
      id: string;
      firstName: string | null;
      lastName: string | null;
      email: string;
    } | null;
  };
  stats: {
    activeProjectsCount: number;
    membersCount: number;
  };
  lists: {
    activeProjects: Array<{
      id: string;
      name: string;
      status: string;
    }>;
  };
  /** null = not computed (the count could not be read); never render it as 0. */
  topRisksCount: number | null;
  executionSummary?: ExecutionSummary; // PHASE 7 MODULE 7.3: Execution signals
};

function countOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function normalizeExecutionSummary(summary?: Partial<ExecutionSummary>): ExecutionSummary | undefined {
  if (!summary) return undefined;
  return {
    version: summary.version ?? 1,
    counts: {
      activeProjects: countOrNull(summary.counts?.activeProjects),
      totalWorkItems: countOrNull(summary.counts?.totalWorkItems),
      overdueWorkItems: countOrNull(summary.counts?.overdueWorkItems),
      dueSoon7Days: countOrNull(summary.counts?.dueSoon7Days),
      inProgress: countOrNull(summary.counts?.inProgress),
      doneLast7Days: countOrNull(summary.counts?.doneLast7Days),
    },
    topOverdue: Array.isArray(summary.topOverdue) ? summary.topOverdue : null,
    recentActivity: Array.isArray(summary.recentActivity) ? summary.recentActivity : null,
  };
}

// Use the Member type from workspace.api.ts
type Member = WorkspaceMember;

function personLabel(person?: {
  name?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
} | null): string | null {
  if (!person) return null;
  if (person.name?.trim()) return person.name.trim();
  const joined = [person.firstName, person.lastName].filter(Boolean).join(' ').trim();
  if (joined) return joined;
  return person.email?.trim() || null;
}

/** Owner line. Null when nobody is named — never the word Unknown. */
export function workspaceOwnerLabel(
  owner: Workspace['owner'] | null | undefined,
  members: Member[],
): string | null {
  const direct = personLabel(owner);
  if (direct) return direct;
  const names = members
    .filter((member) => /^(workspace_owner|owner)$/i.test(String(member.role || '')))
    .map((member) => personLabel(member) || personLabel(member.user))
    .filter((name): name is string => Boolean(name));
  return names.length > 0 ? Array.from(new Set(names)).join(', ') : null;
}

export default function WorkspaceHome() {
  const workspaceId = useWorkspaceStore(s => s.activeWorkspaceId);
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { role: fetchedRole, canWrite, loading: roleLoading } = useWorkspaceRole(workspaceId);
  const permissions = useWorkspacePermissions();

  const { slug } = useParams<{ slug: string }>();
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [workspaceHomeData, setWorkspaceHomeData] = useState<WorkspaceHomeData | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [allProjects, setAllProjects] = useState<Project[]>([]); // Full list for metrics
  const [members, setMembers] = useState<Member[]>([]);
  const [totalMemberCount, setTotalMemberCount] = useState(0);
  // A failed read is not an empty list: these flip when the projects / members fetch fails.
  const [projectsFailed, setProjectsFailed] = useState(false);
  const [membersFailed, setMembersFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [showTemplateCenter, setShowTemplateCenter] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateTarget, setDuplicateTarget] = useState<{ id: string; name: string } | null>(null);
  const [projectOverflowId, setProjectOverflowId] = useState<string | null>(null);
  const [projectFilter, setProjectFilter] = useState<'all' | 'standalone' | 'linked' | null>(null);

  useEffect(() => {
    if (authLoading || !user) {
      setLoading(false);
      return;
    }

    loadWorkspaceData();
  }, [authLoading, user, workspaceId, slug]);

  // Track workspace visits for /home page
  useWorkspaceVisitTracker(
    workspaceHomeData?.workspace
      ? { id: workspaceHomeData.workspace.id, slug: workspaceHomeData.workspace.slug, name: workspaceHomeData.workspace.name }
      : workspace && slug
        ? { id: workspace.id, slug, name: workspace.name }
        : null
  );

  /**
   * Resolve workspace ID from slug if slug is provided
   * Returns the resolved workspace ID or null if not found
   */
  async function resolveWorkspaceIdFromSlug(slug: string): Promise<string | null> {
    try {
      const response = await request.get<{ id: string; slug: string; name: string }>(`/workspaces/slug/${slug}`);
      return response?.id || null;
    } catch (error) {
      console.error('Failed to resolve workspace from slug:', error);
      return null;
    }
  }

  async function loadWorkspaceData() {
    setLoading(true);
    try {
      let effectiveWorkspaceId = workspaceId;

      // Step 1: If slug exists, resolve slug to id first
      if (slug) {
        const resolvedId = await resolveWorkspaceIdFromSlug(slug);
        if (resolvedId) {
          effectiveWorkspaceId = resolvedId;
          // Step 2: If resolved id differs from store, update store
          if (resolvedId !== workspaceId) {
            useWorkspaceStore.getState().setActiveWorkspace(resolvedId);
          }
        } else {
          // Slug resolution failed - cannot proceed
          setWorkspace(null);
          return;
        }
      }

      // Step 3: If no effective workspace ID, cannot proceed
      if (!effectiveWorkspaceId) {
        setWorkspace(null);
        return;
      }

      // Step 4: Use effectiveWorkspaceId for all subsequent calls
      // PHASE 6 MODULE 2: Use workspace home endpoint if slug available
      let homeData: WorkspaceHomeData | null = null;
      if (slug) {
        try {
          const homePayload = await request.get<WorkspaceHomeData | { data: WorkspaceHomeData }>(
            `/workspaces/slug/${slug}/home`,
          );
          homeData =
            homePayload &&
            typeof homePayload === 'object' &&
            'data' in (homePayload as Record<string, unknown>)
              ? ((homePayload as { data: WorkspaceHomeData }).data ?? null)
              : (homePayload as WorkspaceHomeData);
          if (homeData) {
            homeData = {
              ...homeData,
              executionSummary: normalizeExecutionSummary(homeData.executionSummary),
            };
          }
          setWorkspaceHomeData(homeData);
          if (homeData?.workspace) {
            const ws: Workspace = {
              id: homeData.workspace.id,
              name: homeData.workspace.name,
              description: homeData.workspace.description || undefined,
              owner: homeData.workspace.owner ? {
                id: homeData.workspace.owner.id,
                firstName: homeData.workspace.owner.firstName || undefined,
                lastName: homeData.workspace.owner.lastName || undefined,
                email: homeData.workspace.owner.email,
              } : undefined,
            };
            setWorkspace(ws);
            // Get workspace notes separately using effectiveWorkspaceId
            const wsDetail = await getWorkspace(effectiveWorkspaceId).catch(() => null);
            if (wsDetail?.homeNotes) {
              setNotesValue(wsDetail.homeNotes);
            }
          }
        } catch (error) {
          console.warn('Failed to load workspace home endpoint, falling back to individual calls:', error);
        }
      }

      // Always fetch full project list for metrics (includes programId/portfolioId)
      // Use effectiveWorkspaceId for all calls
      const [ws, projs, mems] = await Promise.all([
        !homeData ? getWorkspace(effectiveWorkspaceId).catch(() => null) : Promise.resolve(null),
        listProjects(effectiveWorkspaceId).catch(() => null),
        listWorkspaceMembers(effectiveWorkspaceId).catch(() => null),
      ]);
      setProjectsFailed(!Array.isArray(projs));
      setMembersFailed(!Array.isArray(mems));

      if (!homeData && ws) {
        setWorkspace(ws);
        setNotesValue(ws.homeNotes || '');
      }

      // Store full project list for metrics — enforce array
      const safeProjs = Array.isArray(projs) ? projs : [];
      const safeMems = Array.isArray(mems) ? mems : [];
      setAllProjects(safeProjs);

      // Apply filter if set
      let filteredProjects = safeProjs;
      if (projectFilter === 'standalone') {
        filteredProjects = safeProjs.filter(p => !p.programId && !p.portfolioId);
      } else if (projectFilter === 'linked') {
        filteredProjects = safeProjs.filter(p => p.programId || p.portfolioId);
      }

      setProjects(filteredProjects.slice(0, 50));
      setMembers(safeMems.slice(0, 5));
      setTotalMemberCount(safeMems.length);
    } catch (error) {
      console.error('Failed to load workspace data:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleSaveNotes() {
    if (!workspaceId || !workspace) return;

    setSavingNotes(true);
    try {
      const updated = await updateWorkspace(workspaceId, { homeNotes: notesValue });
      setWorkspace(updated);
      setEditingNotes(false);
    } catch (error) {
      console.error('Failed to save notes:', error);
      toast.error('Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  }

  function getMemberName(member: Member): string {
    if (member.name) return member.name;
    if (member.user?.name) return member.user.name;
    if (member.user?.firstName || member.user?.lastName) {
      return [member.user.firstName, member.user.lastName].filter(Boolean).join(' ');
    }
    return member.email || member.user?.email || 'Unknown';
  }

  function getDeliveryOwnerName(project: Project): string {
    if (!project.deliveryOwner) return '—';
    if (project.deliveryOwner.name) return project.deliveryOwner.name;
    if (project.deliveryOwner.firstName || project.deliveryOwner.lastName) {
      return [project.deliveryOwner.firstName, project.deliveryOwner.lastName].filter(Boolean).join(' ');
    }
    return project.deliveryOwner.email || '—';
  }

  function computeMetrics() {
    const standaloneProjects = allProjects.filter(p => !p.programId && !p.portfolioId);
    const linkedProjects = allProjects.filter(p => p.programId || p.portfolioId);

    // null stays null: "not computed" is a different fact from 0.
    const risksOpen = workspaceHomeData?.topRisksCount ?? null;

    // A failed project list is not "0 projects": every count from it is null.
    return {
      activeProjects: projectsFailed ? null : activeProjectMetric(allProjects),
      standaloneProjects: projectsFailed ? null : standaloneProjects.length,
      linkedProjects: projectsFailed ? null : linkedProjects.length,
      totalProjects: projectsFailed ? null : allProjects.length,
      risksOpen,
    };
  }

  function getProjectTag(project: Project): string {
    if (project.programId && project.program) {
      return `Program: ${project.program.name}`;
    }
    if (project.portfolioId && project.portfolio) {
      return `Portfolio: ${project.portfolio.name}`;
    }
    return 'Standalone';
  }

  // PHASE 7 MODULE 7.3: Format activity text
  function formatActivityText(activity: NonNullable<ExecutionSummary['recentActivity']>[0]): string {
    const actor = activity.actorName;
    const task = activity.workItemTitle;

    return `${actor} ${taskActivityPhrase(activity.type)} "${task}"`;
  }

  if (!workspaceId) {
    return (
      <div className="p-6" data-testid="workspace-home">
        <div className="text-center text-gray-500">
          <p>Select a workspace to view its overview</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6" data-testid="workspace-home">
        <div className="text-center text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!workspace) {
    return (
      <div className="p-6" data-testid="workspace-home">
        <div className="max-w-2xl mx-auto text-center py-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            Workspace not found
          </h2>
          <p className="text-gray-600 mb-4">
            The workspace you're looking for doesn't exist or you don't have access to it.
          </p>
          <Button onClick={() => navigate('/workspaces')}>
            Back to Workspaces
          </Button>
        </div>
      </div>
    );
  }

  const isOwner = permissions.workspacePermission === 'owner' || fetchedRole === 'OWNER' || fetchedRole === 'ADMIN';
  const canStartWork =
    canWrite ||
    permissions.canCreateWork ||
    fetchedRole === 'OWNER' ||
    fetchedRole === 'ADMIN' ||
    fetchedRole === 'MEMBER';
  const ownerLabel = workspaceOwnerLabel(workspace.owner, members);

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto" data-testid="workspace-home">
      {/* PROMPT 5 Part B: Header block with actions */}
      <div className="border-b pb-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1">
            <h1 className="text-2xl font-semibold text-gray-900 mb-1">
              {workspace.name}
            </h1>
            {ownerLabel ? (
              <p className="text-sm text-gray-500">Owner: {ownerLabel}</p>
            ) : null}
          </div>
          {isOwner && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => navigate(`/workspaces/${workspaceId}/members`)}
                className="px-4 py-2"
              >
                Invite
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate(`/workspaces/${workspaceId}/members`)}
                className="px-4 py-2"
              >
                Members
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate(`/workspaces/${workspaceId}/settings`)}
                className="px-4 py-2"
              >
                Settings
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* PROMPT 5 Part C.1: Start work section */}
      <div className="border-b pb-4">
        {canStartWork ? (
          <div className="flex items-center gap-4">
            <Button
              onClick={() => setShowTemplateCenter(true)}
              className="px-6 py-2"
            >
              {NEW_PROJECT_ACTION_LABEL}
            </Button>
          </div>
        ) : !roleLoading ? (
          <p className="text-gray-600">Ask a workspace owner to start work</p>
        ) : null}
      </div>

      {/* PHASE 7 MODULE 7.3: Execution Summary Widgets */}
      {workspaceHomeData?.executionSummary && (
        <div className="border-b pb-4">
          <h2 className="text-lg font-medium text-gray-900 mb-3">Execution Signals</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <WidgetCard
              title="Active Projects"
              value={projectsFailed ? null : activeProjectMetric(allProjects)}
            />
            <WidgetCard
              title="Total Tasks"
              value={workspaceHomeData.executionSummary.counts.totalWorkItems}
            />
            <WidgetCard
              title="Overdue"
              value={workspaceHomeData.executionSummary.counts.overdueWorkItems}
              variant={(workspaceHomeData.executionSummary.counts.overdueWorkItems ?? 0) > 0 ? 'danger' : undefined}
            />
            <WidgetCard
              title="Due Soon"
              value={workspaceHomeData.executionSummary.counts.dueSoon7Days}
              variant={(workspaceHomeData.executionSummary.counts.dueSoon7Days ?? 0) > 0 ? 'warning' : undefined}
            />
            <WidgetCard
              title="In Progress"
              value={workspaceHomeData.executionSummary.counts.inProgress}
            />
            <WidgetCard
              title="Done (7d)"
              value={workspaceHomeData.executionSummary.counts.doneLast7Days}
            />
            {/* The risks tile belongs to BOTH layouts: it used to sit only in the
                fallback block, so it never painted when executionSummary was present. */}
            <RisksOpenTile count={workspaceHomeData.topRisksCount} />
          </div>
        </div>
      )}

      {/* PHASE 6 MODULE 2: Project Overview Widgets (fallback if no executionSummary) */}
      {!workspaceHomeData?.executionSummary && (
        <div className="border-b pb-4">
          <h2 className="text-lg font-medium text-gray-900 mb-3">Overview</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {(() => {
              const metrics = computeMetrics();
              return (
                <>
                  <WidgetCard
                    title="Active Projects"
                    value={metrics.activeProjects}
                    onClick={() => {
                      if (projectFilter === 'all') {
                        setProjectFilter(null);
                      } else {
                        setProjectFilter('all');
                      }
                      setTimeout(() => loadWorkspaceData(), 0);
                    }}
                    className="cursor-pointer hover:bg-gray-50"
                  />
                  <WidgetCard
                    title="Standalone"
                    value={metrics.standaloneProjects}
                    onClick={() => {
                      if (projectFilter === 'standalone') {
                        setProjectFilter(null);
                      } else {
                        setProjectFilter('standalone');
                      }
                      setTimeout(() => loadWorkspaceData(), 0);
                    }}
                    className="cursor-pointer hover:bg-gray-50"
                  />
                  <WidgetCard
                    title="Linked"
                    value={metrics.linkedProjects}
                    onClick={() => {
                      if (projectFilter === 'linked') {
                        setProjectFilter(null);
                      } else {
                        setProjectFilter('linked');
                      }
                      setTimeout(() => loadWorkspaceData(), 0);
                    }}
                    className="cursor-pointer hover:bg-gray-50"
                  />
                  {workspaceHomeData && (
                    <RisksOpenTile count={workspaceHomeData.topRisksCount} />
                  )}
                </>
              );
            })()}
          </div>
          {projectFilter && (
            <div className="mt-2 text-sm text-gray-600">
              Showing: {projectFilter === 'standalone' ? 'Standalone projects' : projectFilter === 'linked' ? 'Linked projects' : 'All projects'}
              <button
                onClick={() => {
                  setProjectFilter(null);
                  loadWorkspaceData();
                }}
                className="ml-2 text-blue-600 hover:text-blue-800"
              >
                Clear filter
              </button>
            </div>
          )}
        </div>
      )}

      {/* PHASE 7 MODULE 7.3: Overdue Tasks and Recent Activity Panels */}
      {workspaceHomeData?.executionSummary && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 border-b pb-4">
          {/* Overdue Tasks Panel */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Overdue Tasks
              {(workspaceHomeData.executionSummary.counts.overdueWorkItems ?? 0) > 0 && (
                <span className="ml-2 text-sm font-normal text-red-600">
                  ({workspaceHomeData.executionSummary.counts.overdueWorkItems})
                </span>
              )}
            </h3>
            {!workspaceHomeData.executionSummary.topOverdue ? (
              <UnavailablePanel onRetry={() => void loadWorkspaceData()} testId="home-overdue-unavailable" />
            ) : workspaceHomeData.executionSummary.topOverdue.length === 0 ? (
              <p className="text-sm text-gray-500">No overdue tasks</p>
            ) : (
              <div className="space-y-3">
                {workspaceHomeData.executionSummary.topOverdue.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => navigate(`/projects/${item.projectId}?taskId=${item.id}`)}
                    className="p-3 border border-red-200 rounded-lg hover:bg-red-50 cursor-pointer transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{item.title}</div>
                        <div className="text-sm text-gray-600 mt-1">
                          {item.projectName}
                          {item.assigneeName && ` • ${item.assigneeName}`}
                        </div>
                        {item.dueDate && (
                          <div className="text-xs text-red-600 mt-1">
                            Due: {formatCalendarDate(item.dueDate)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent Activity Panel */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
            {!workspaceHomeData.executionSummary.recentActivity ? (
              <UnavailablePanel onRetry={() => void loadWorkspaceData()} testId="home-activity-unavailable" />
            ) : workspaceHomeData.executionSummary.recentActivity.length === 0 ? (
              <p className="text-sm text-gray-500">No recent activity</p>
            ) : (
              <div className="space-y-3">
                {workspaceHomeData.executionSummary.recentActivity.map((activity) => {
                  const activityText = formatActivityText(activity);
                  return (
                    <div key={`${activity.workItemId}-${activity.createdAt}`} className="text-sm">
                      <div className="text-gray-900">{activityText}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {activity.projectName} • {new Date(activity.createdAt).toLocaleString()}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* PROMPT 5 Part C.2: Workspace notes section */}
      <div className="border-b pb-4">
        <h2 className="text-lg font-medium text-gray-900 mb-2">
          Workspace notes
        </h2>
        {editingNotes ? (
          <div className="space-y-2">
            <textarea
              value={notesValue}
              onChange={(e) => setNotesValue(e.target.value)}
              className="w-full min-h-[120px] p-3 border rounded-md resize-y"
              placeholder="Add workspace notes, rules, links, expectations..."
            />
            <div className="flex gap-2">
              <Button
                onClick={handleSaveNotes}
                disabled={savingNotes}
                className="px-4 py-1 text-sm"
              >
                {savingNotes ? 'Saving...' : 'Save'}
              </Button>
              <Button
                onClick={() => {
                  setNotesValue(workspace.homeNotes || '');
                  setEditingNotes(false);
                }}
                variant="ghost"
                className="px-4 py-1 text-sm"
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div>
            {workspace.homeNotes ? (
              <p className="text-gray-700 whitespace-pre-wrap">{workspace.homeNotes}</p>
            ) : (
              <p className="text-gray-400 italic">No notes yet.</p>
            )}
            {isOwner && (
              <button
                onClick={() => setEditingNotes(true)}
                className="mt-2 text-sm text-blue-600 hover:text-blue-800"
              >
                Edit notes
              </button>
            )}
          </div>
        )}
      </div>

      {/* PROMPT 5 Part C.3: Projects snapshot */}
      <div className="border-b pb-4">
        <h2 className="text-lg font-medium text-gray-900 mb-3">Projects</h2>
        {projectsFailed ? (
          <UnavailablePanel onRetry={() => void loadWorkspaceData()} testId="home-projects-unavailable" />
        ) : projects.length === 0 ? (
          <div>
            {canStartWork ? (
              <Button
                onClick={() => setShowTemplateCenter(true)}
                variant="outline"
                className="px-4 py-2"
              >
                Start from template
              </Button>
            ) : (
              <p className="text-gray-500">No projects yet.</p>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {projects.map((project) => (
              <div
                key={project.id}
                className="flex items-center justify-between p-3 border rounded hover:bg-gray-50 group"
              >
                <Link
                  to={`/projects/${project.id}`}
                  className="flex-1"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{project.name}</span>
                    <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                      {getProjectTag(project)}
                    </span>
                  </div>
                  <div className="text-sm text-gray-500">
                    {projectStateLabel(project.state)} · {lifecycleHealthLabel(project.state, project.health)} · {getDeliveryOwnerName(project)}
                  </div>
                </Link>
                {/* Overflow menu */}
                <div className="relative ml-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setProjectOverflowId(projectOverflowId === project.id ? null : project.id);
                    }}
                    className="p-1 rounded hover:bg-gray-200 opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="More actions" title="More actions"
                  >
                    <MoreHorizontal className="h-4 w-4 text-gray-500" />
                  </button>
                  {projectOverflowId === project.id && (
                    <div className="absolute right-0 top-full mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          setProjectOverflowId(null);
                          setDuplicateTarget({ id: project.id, name: project.name });
                          setShowDuplicateModal(true);
                        }}
                        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <Copy className="w-4 h-4" />
                        Duplicate
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* PROMPT 5 Part C.4: Members snapshot */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-medium text-gray-900">
            Members {!membersFailed && totalMemberCount > 0 && <span className="text-sm text-gray-500 font-normal">({totalMemberCount})</span>}
          </h2>
          <div className="flex items-center gap-2">
            {isOwner && (
              <Button
                variant="outline"
                onClick={() => setShowInviteModal(true)}
                className="px-3 py-1 text-sm"
              >
                Invite members
              </Button>
            )}
            {isOwner ? (
              <Link
                to={`/workspaces/${workspaceId}/members`}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Manage members
              </Link>
            ) : (
              <Link
                to={`/workspaces/${workspaceId}/members`}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                View members
              </Link>
            )}
          </div>
        </div>
        {membersFailed ? (
          <UnavailablePanel onRetry={() => void loadWorkspaceData()} testId="home-members-unavailable" />
        ) : members.length === 0 ? (
          <p className="text-gray-500">No members yet.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {members.map((member) => (
              <div
                key={member.id}
                className="px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-700"
              >
                {getMemberName(member)}
              </div>
            ))}
            {totalMemberCount > 5 && (
              <Link
                to={`/workspaces/${workspaceId}/members`}
                className="px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-700 hover:bg-gray-200"
              >
                +{totalMemberCount - 5} more
              </Link>
            )}
          </div>
        )}
      </div>

      {/* Template Center — ADR-004 single create funnel */}
      {showTemplateCenter && workspaceId ? (
        <TemplateCenterModal
          open={showTemplateCenter}
          onClose={() => setShowTemplateCenter(false)}
          workspaceId={workspaceId}
        />
      ) : null}

      {/* PROMPT 6 C2: Invite Members Modal */}
      {showInviteModal && (
        <WorkspaceMemberInviteModal
          open={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          onInvite={async (userId: string, accessLevel: 'Owner' | 'Member' | 'Guest') => {
            if (!workspaceId) return;
            try {
              const role = mapAccessLevelToRole(accessLevel);
              await addWorkspaceMember(workspaceId, userId, role as any);
              toast.success('Member added successfully');
              setShowInviteModal(false);
              loadWorkspaceData();
            } catch (error: any) {
              console.error('Failed to add member:', error);
              const errorCode = error?.response?.data?.code;
              const errorMessage = error?.response?.data?.message;
              const displayMessage = errorMessage || 'Failed to add member';
              toast.error(displayMessage);
            }
          }}
          workspaceId={workspaceId}
        />
      )}

      {/* Duplicate Project Modal */}
      {duplicateTarget && workspaceId && (
        <DuplicateProjectModal
          open={showDuplicateModal}
          onClose={() => {
            setShowDuplicateModal(false);
            setDuplicateTarget(null);
          }}
          projectId={duplicateTarget.id}
          projectName={duplicateTarget.name}
          workspaceId={workspaceId}
        />
      )}
    </div>
  );
}

// PHASE 6 MODULE 2: Widget Card Component

/**
 * "Risks Open": a real count (including a real 0), or Unavailable when the
 * server could not compute it. null is a different fact from 0 — never paint it as 0.
 */
export function RisksOpenTile({ count }: { count: number | null | undefined }) {
  if (count === null || count === undefined) {
    return <WidgetCard title="Risks Open" value="Unavailable" variant="default" />;
  }
  return (
    <WidgetCard
      title="Risks Open"
      value={count}
      variant={count > 0 ? 'warning' : undefined}
    />
  );
}

function WidgetCard({
  title,
  value,
  onClick,
  className = '',
  variant = 'default'
}: {
  title: string;
  value: number | string | null;
  onClick?: () => void;
  className?: string;
  variant?: 'default' | 'warning' | 'danger';
}) {
  const baseClasses = "p-4 border rounded-lg bg-white";
  const variantClasses = {
    default: "border-gray-200",
    warning: "border-yellow-200 bg-yellow-50",
    danger: "border-red-200 bg-red-50",
  };

  return (
    <div
      className={`${baseClasses} ${variantClasses[variant]} ${className}`}
      onClick={onClick}
    >
      <div className="text-sm text-gray-600 mb-1">{title}</div>
      <div className={`text-2xl font-semibold ${onClick ? 'text-blue-600' : 'text-gray-900'}`}>
        {value === null || value === undefined ? <UnavailableValue /> : value}
      </div>
    </div>
  );
}
